import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../../../components/layout/MainLayout';
import { getBackofficeSidebarItems } from '../../shell/backofficeSidebar';
import { useAuth } from '../../../../context/AuthContext';
import { PaginatedListLoading } from '../../../../components/ui/PaginatedListLoading';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type ActivityRow = {
  id: string;
  createdAt: string;
  severity: string;
  category: string;
  action: string;
  actorUserId: string | null;
  actorType: string;
  actorCode?: string | null;
  actorEmailMasked: string | null;
  targetUserId: string | null;
  subjectType: string | null;
  subjectId: string | null;
  message: string | null;
  metadata: unknown;
  ipAddress: string | null;
  userAgent: string | null;
  requestPath: string | null;
  requestMethod: string | null;
  responseStatus: number | null;
};

type ListResponse = {
  total: number;
  page: number;
  pageSize: number;
  items: ActivityRow[];
};

const emptyDraft = () => ({
  q: '',
  from: '',
  to: '',
  category: '',
  action: '',
  actorType: '',
  actorUserId: '',
  targetUserId: '',
  ip: '',
  ipContains: '',
  severity: '',
  requestMethod: '',
  requestPathContains: '',
  responseStatus: '',
  subjectType: '',
  subjectId: '',
});

export default function WorkspaceActivitiesPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { authorizedFetch } = useAuth();
  const [draft, setDraft] = useState(emptyDraft);
  const [applied, setApplied] = useState(emptyDraft);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const isStaffActionAuditRoute = location.pathname.includes('/security/action-audit');
  const globalAuthorizedFetch = useCallback(
    (input: RequestInfo | URL, init: RequestInit = {}) => {
      const headers = new Headers(init.headers || {});
      // Activities and action-audit are intentionally global (not department-scoped).
      headers.set('x-department-slug', '');
      return authorizedFetch(input, { ...init, headers });
    },
    [authorizedFetch],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = new URL(`${API_BASE}/activities/workspace`);
      url.searchParams.set('page', String(page));
      url.searchParams.set('pageSize', String(pageSize));
      if (applied.q.trim()) url.searchParams.set('q', applied.q.trim());
      if (applied.from) url.searchParams.set('from', `${applied.from}T00:00:00.000Z`);
      if (applied.to) url.searchParams.set('to', `${applied.to}T23:59:59.999Z`);
      if (applied.category.trim()) url.searchParams.set('category', applied.category.trim());
      if (applied.action.trim()) url.searchParams.set('action', applied.action.trim());
      if (applied.actorType) url.searchParams.set('actorType', applied.actorType);
      if (applied.actorUserId.trim()) url.searchParams.set('actorUserId', applied.actorUserId.trim());
      if (applied.targetUserId.trim()) url.searchParams.set('targetUserId', applied.targetUserId.trim());
      if (applied.ip.trim()) url.searchParams.set('ip', applied.ip.trim());
      if (applied.ipContains.trim()) url.searchParams.set('ipContains', applied.ipContains.trim());
      if (applied.severity) url.searchParams.set('severity', applied.severity);
      if (applied.requestMethod.trim()) url.searchParams.set('requestMethod', applied.requestMethod.trim());
      if (applied.requestPathContains.trim()) url.searchParams.set('requestPathContains', applied.requestPathContains.trim());
      if (applied.responseStatus.trim()) url.searchParams.set('responseStatus', applied.responseStatus.trim());
      if (applied.subjectType.trim()) url.searchParams.set('subjectType', applied.subjectType.trim());
      if (applied.subjectId.trim()) url.searchParams.set('subjectId', applied.subjectId.trim());

      const res = await globalAuthorizedFetch(url.toString());
      if (!res.ok) throw new Error('load');
      setData((await res.json()) as ListResponse);
    } catch {
      setError(t('backoffice.activities_load_error'));
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [applied, globalAuthorizedFetch, page, pageSize, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const applyFilters = () => {
    setApplied({ ...draft });
    setPage(1);
  };

  const clearFilters = () => {
    const z = emptyDraft();
    setDraft(z);
    setApplied(z);
    setPage(1);
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  const severityClass = (s: string) => {
    switch (s) {
      case 'CRITICAL':
      case 'SECURITY':
        return 'bg-rose-50 text-rose-800';
      case 'WARNING':
        return 'bg-amber-50 text-amber-900';
      case 'NOTICE':
        return 'bg-sky-50 text-sky-900';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  const extractOrderStatusChange = (metadata: unknown): { orderId: string | null; toStatus: string | null } | null => {
    if (!metadata || typeof metadata !== 'object') return null;
    const container = metadata as Record<string, unknown>;
    const node = container.orderStatusChange;
    if (!node || typeof node !== 'object') return null;
    const change = node as Record<string, unknown>;
    return {
      orderId: typeof change.orderId === 'string' ? change.orderId : null,
      toStatus: typeof change.toStatus === 'string' ? change.toStatus : null,
    };
  };

  return (
    <MainLayout
      withSidebar
      sidebarItems={getBackofficeSidebarItems(t)}
      activePath={`${location.pathname}${location.search}`}
    >
      <main className="workspace-page">
        <section>
          <h1 className="workspace-page-title">
            {isStaffActionAuditRoute
              ? t('backoffice.staff_action_audit_title', { defaultValue: 'Staff action audit' })
              : t('backoffice.activities_title')}
          </h1>
          <p className="workspace-page-subtitle text-slate-600">
            {isStaffActionAuditRoute
              ? t('backoffice.staff_action_audit_subtitle', {
                  defaultValue:
                    'Review employee/staff create, update, and delete actions with actor, route, status, and metadata.',
                })
              : t('backoffice.activities_subtitle')}
          </p>
        </section>

        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_q')}
              </label>
              <input
                value={draft.q}
                onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('backoffice.activities_filter_q_ph')}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_category')}
              </label>
              <input
                value={draft.category}
                onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="iam, customer, …"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_action')}
              </label>
              <input
                value={draft.action}
                onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder={t('backoffice.activities_filter_action_ph')}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_severity')}
              </label>
              <select
                value={draft.severity}
                onChange={(e) => setDraft((d) => ({ ...d, severity: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="">{t('common.all')}</option>
                <option value="INFO">INFO</option>
                <option value="NOTICE">NOTICE</option>
                <option value="WARNING">WARNING</option>
                <option value="SECURITY">SECURITY</option>
                <option value="CRITICAL">CRITICAL</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_from')}
              </label>
              <input
                type="date"
                value={draft.from}
                onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_to')}
              </label>
              <input
                type="date"
                value={draft.to}
                onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_actor_type', { defaultValue: 'Actor type' })}
              </label>
              <select
                value={draft.actorType}
                onChange={(e) => setDraft((d) => ({ ...d, actorType: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="">{t('common.all')}</option>
                <option value="EMPLOYEE">EMPLOYEE</option>
                <option value="ADMIN">ADMIN</option>
                <option value="SHIPPING_AGENT">SHIPPING_AGENT</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_actor')}
              </label>
              <input
                value={draft.actorUserId}
                onChange={(e) => setDraft((d) => ({ ...d, actorUserId: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
                placeholder="user id"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_target')}
              </label>
              <input
                value={draft.targetUserId}
                onChange={(e) => setDraft((d) => ({ ...d, targetUserId: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
                placeholder="user id"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_request_method', { defaultValue: 'Request method' })}
              </label>
              <select
                value={draft.requestMethod}
                onChange={(e) => setDraft((d) => ({ ...d, requestMethod: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="">{t('common.all')}</option>
                <option value="POST">POST</option>
                <option value="PATCH">PATCH</option>
                <option value="PUT">PUT</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_request_path', { defaultValue: 'Path contains' })}
              </label>
              <input
                value={draft.requestPathContains}
                onChange={(e) => setDraft((d) => ({ ...d, requestPathContains: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="/sales/orders"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_response_status', { defaultValue: 'Response status' })}
              </label>
              <input
                value={draft.responseStatus}
                onChange={(e) => setDraft((d) => ({ ...d, responseStatus: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
                placeholder="200 / 403 / 500"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_ip')}
              </label>
              <input
                value={draft.ip}
                onChange={(e) => setDraft((d) => ({ ...d, ip: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
                placeholder="exact"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_ip_contains')}
              </label>
              <input
                value={draft.ipContains}
                onChange={(e) => setDraft((d) => ({ ...d, ipContains: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
                placeholder="192.168"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_subject_type')}
              </label>
              <input
                value={draft.subjectType}
                onChange={(e) => setDraft((d) => ({ ...d, subjectType: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">
                {t('backoffice.activities_filter_subject_id')}
              </label>
              <input
                value={draft.subjectId}
                onChange={(e) => setDraft((d) => ({ ...d, subjectId: e.target.value }))}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 font-mono text-xs"
              />
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  {t('backoffice.activities_page_size')}
                </label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
            <button
              type="button"
              onClick={() => void applyFilters()}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800"
            >
              {t('backoffice.activities_apply')}
            </button>
            <button
              type="button"
              onClick={() => void clearFilters()}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              {t('backoffice.activities_clear')}
            </button>
          </div>
        </div>

        {error ? <p className="text-sm text-red-600">{error}</p> : null}

        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-3 py-2">{t('backoffice.activities_col_time')}</th>
                <th className="px-3 py-2">{t('backoffice.activities_col_severity')}</th>
                <th className="px-3 py-2">{t('backoffice.activities_col_category')}</th>
                <th className="px-3 py-2">{t('backoffice.activities_col_action')}</th>
                <th className="px-3 py-2">{t('backoffice.activities_col_actor')}</th>
                <th className="px-3 py-2">{t('backoffice.activities_col_ip')}</th>
                <th className="px-3 py-2">{t('backoffice.activities_col_message')}</th>
                <th className="px-3 py-2 w-24" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <PaginatedListLoading tableColSpan={8} title={t('common.list_loading_title')} />
              ) : !data?.items.length ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-slate-500">
                    {t('backoffice.activities_empty')}
                  </td>
                </tr>
              ) : (
                data.items.map((row) => (
                  <React.Fragment key={row.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-700">
                        {new Date(row.createdAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${severityClass(row.severity)}`}
                        >
                          {row.severity}
                        </span>
                      </td>
                      <td className="max-w-[8rem] truncate px-3 py-2 font-mono text-xs text-slate-800">
                        {row.category}
                      </td>
                      <td className="max-w-[12rem] truncate px-3 py-2 text-xs text-slate-800" title={row.action}>
                        {row.action}
                      </td>
                      <td className="max-w-[10rem] px-3 py-2 text-xs text-slate-700">
                        <div className="truncate" title={row.actorEmailMasked || row.actorUserId || ''}>
                          {row.actorEmailMasked || '—'}
                        </div>
                        <div className="truncate font-mono text-[10px] text-slate-500">
                          {row.actorType}
                          {row.actorUserId ? ` · ${row.actorUserId.slice(0, 8)}…` : ''}
                        </div>
                        <div className="truncate font-mono text-[10px] text-slate-500" title={row.actorCode || ''}>
                          {row.actorCode ? `code: ${row.actorCode}` : 'code: —'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-700">
                        {row.ipAddress || '—'}
                      </td>
                      <td className="max-w-[14rem] truncate px-3 py-2 text-xs text-slate-600" title={row.message || ''}>
                        {extractOrderStatusChange(row.metadata)?.toStatus
                          ? `Status -> ${extractOrderStatusChange(row.metadata)?.toStatus}`
                          : (row.message || '—')}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => setExpandedId((id) => (id === row.id ? null : row.id))}
                          className="text-xs font-medium text-[#00b853] hover:underline"
                        >
                          {expandedId === row.id ? t('common.collapse') : t('common.expand')}
                        </button>
                      </td>
                    </tr>
                    {expandedId === row.id ? (
                      <tr className="border-b border-slate-100 bg-slate-50/90">
                        <td colSpan={8} className="px-3 py-3 text-xs text-slate-800">
                          <div className="grid gap-3 md:grid-cols-2">
                            <div>
                              <div className="mb-1 font-semibold text-slate-600">
                                {t('backoffice.activities_detail_ids')}
                              </div>
                              <dl className="space-y-1 font-mono text-[11px]">
                                <div>
                                  <dt className="inline text-slate-500">id: </dt>
                                  <dd className="inline break-all">{row.id}</dd>
                                </div>
                                <div>
                                  <dt className="inline text-slate-500">actorUserId: </dt>
                                  <dd className="inline break-all">{row.actorUserId || '—'}</dd>
                                </div>
                                <div>
                                  <dt className="inline text-slate-500">actorCode: </dt>
                                  <dd className="inline break-all">{row.actorCode || '—'}</dd>
                                </div>
                                <div>
                                  <dt className="inline text-slate-500">targetUserId: </dt>
                                  <dd className="inline break-all">{row.targetUserId || '—'}</dd>
                                </div>
                                <div>
                                  <dt className="inline text-slate-500">subject: </dt>
                                  <dd className="inline break-all">
                                    {(row.subjectType || '—') + ' / ' + (row.subjectId || '—')}
                                  </dd>
                                </div>
                              </dl>
                            </div>
                            <div>
                              <div className="mb-1 font-semibold text-slate-600">
                                {t('backoffice.activities_detail_request')}
                              </div>
                              <dl className="space-y-1 font-mono text-[11px]">
                                <div>
                                  <dt className="inline text-slate-500">method: </dt>
                                  <dd className="inline">{row.requestMethod || '—'}</dd>
                                </div>
                                <div>
                                  <dt className="inline text-slate-500">path: </dt>
                                  <dd className="inline break-all">{row.requestPath || '—'}</dd>
                                </div>
                                <div>
                                  <dt className="inline text-slate-500">status: </dt>
                                  <dd className="inline">{row.responseStatus ?? '—'}</dd>
                                </div>
                              </dl>
                            </div>
                          </div>
                          {(() => {
                            const statusChange = extractOrderStatusChange(row.metadata);
                            if (!statusChange?.toStatus) return null;
                            return (
                              <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-900">
                                Order {statusChange.orderId || '—'} status changed to{' '}
                                <span className="font-semibold">{statusChange.toStatus}</span>
                              </div>
                            );
                          })()}
                          {row.userAgent ? (
                            <div className="mt-3">
                              <div className="mb-1 font-semibold text-slate-600">User-Agent</div>
                              <pre className="max-h-24 overflow-auto whitespace-pre-wrap break-all rounded border border-slate-200 bg-white p-2 font-mono text-[10px]">
                                {row.userAgent}
                              </pre>
                            </div>
                          ) : null}
                          <div className="mt-3">
                            <div className="mb-1 font-semibold text-slate-600">
                              {t('backoffice.activities_detail_metadata')}
                            </div>
                            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded border border-slate-200 bg-white p-2 font-mono text-[10px]">
                              {row.metadata != null
                                ? JSON.stringify(row.metadata, null, 2)
                                : '—'}
                            </pre>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {data && data.total > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-600">
            <span>
              {t('backoffice.activities_pagination_info', {
                page: data.page,
                totalPages,
                total: data.total,
              })}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-40"
              >
                {t('common.prev')}
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-40"
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        ) : null}
      </main>
    </MainLayout>
  );
}
